/**
 * TrustCenter — /trust-center page for admin/editor/auditor.
 * Displays the org's audit readiness + downloads Auditor Package ZIP.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, Download, ExternalLink, RefreshCw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';

const STATUS_META = {
  ready:            { label: 'Audit Ready',       cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' },
  in_progress:      { label: 'In Progress',       cls: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  needs_attention:  { label: 'Needs Attention',   cls: 'bg-red-500/10 text-red-600 border-red-500/30' },
};

export default function TrustCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pid, setPid] = useState('');
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/trust-center/summary');
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load Trust Center');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadPackage = async () => {
    if (!pid.trim()) { toast.error('Enter a product ID first'); return; }
    setDownloading(true);
    try {
      const r = await api.get(`/auditor-package/${pid.trim()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `audit_package_${pid.trim().slice(0, 8)}.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Audit Package downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Download failed');
    } finally { setDownloading(false); }
  };

  if (loading) {
    return (
      <div className="p-10 flex justify-center" data-testid="trust-loading">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  const s = STATUS_META[data.trust.status] || STATUS_META.needs_attention;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="trust-center">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6" /> Trust Center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time audit-readiness for your entire workspace.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`${s.cls} text-xs`} data-testid="trust-status">
            {s.label}
          </Badge>
          <Button variant="outline" size="sm" onClick={load} data-testid="trust-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Products" value={data.products.total}
                  hint={`${data.products.approved} approved · ${data.products.with_passport} with passport`}
                  testid="tc-stat-products" />
        <StatCard label="Suppliers" value={data.suppliers.total}
                  hint={`${data.suppliers.active} active · ${data.suppliers.verified} verified`}
                  testid="tc-stat-suppliers" />
        <StatCard label="Submissions" value={data.submissions.total}
                  hint={`${data.submissions.approved} approved · ${data.submissions.pending} pending`}
                  testid="tc-stat-submissions" />
        <StatCard label="Avg Trust Score" value={`${data.trust.average_score}%`}
                  hint={`Audit readiness ${data.trust.audit_readiness}%`}
                  testid="tc-stat-trust" bar={data.trust.audit_readiness} />
      </div>

      {/* Readiness detail */}
      <Card className="p-5" data-testid="tc-readiness-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-medium">Audit readiness</h2>
          <span className="text-2xl font-heading font-semibold">{data.trust.audit_readiness}%</span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-2 bg-emerald-500 rounded-full transition-all"
               style={{ width: `${Math.min(100, data.trust.audit_readiness)}%` }} />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Composite of supplier submission approval rate, supplier activation, passport coverage,
          and the average product trust score.
        </p>
      </Card>

      {/* Auditor package */}
      <Card className="p-5" data-testid="tc-package-card">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-medium flex items-center gap-2">
              <Download className="w-4 h-4" /> Auditor package
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              One ZIP with the version history, supplier submissions, audit log, and public
              verification links — the exact bundle a CBAM auditor asks for.
            </p>
          </div>
          <a href="/audit" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            View audit feed <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Input placeholder="Product ID (uuid)" value={pid}
                 onChange={(e) => setPid(e.target.value)}
                 className="max-w-md" data-testid="tc-package-pid-input" />
          <Button onClick={downloadPackage} disabled={downloading || !pid.trim()} data-testid="tc-package-download-btn">
            {downloading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Building…</> : <>Download ZIP</>}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">
          Tip: any auditor can independently verify the chain at{' '}
          <code className="bg-muted px-1 rounded">GET /api/verify/&lt;product_id&gt;/version-chain</code> (no login).
        </p>
      </Card>
    </div>
  );
}

function StatCard({ label, value, hint, testid, bar }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-2xl font-heading font-semibold mt-1">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
      {bar !== undefined && (
        <div className="w-full h-1 bg-muted rounded-full overflow-hidden mt-2">
          <div className="h-1 bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, bar)}%` }} />
        </div>
      )}
    </Card>
  );
}
